import React__default, { createContext, useState, useRef, useEffect, useMemo, Fragment, useContext } from "react";
import { useDisposables as p } from "../../hooks/use-disposables.js";
import { useEvent as o } from "../../hooks/use-event.js";
import { useIsMounted as f } from "../../hooks/use-is-mounted.js";
import { useIsoMorphicEffect as n } from "../../hooks/use-iso-morphic-effect.js";
import { useLatestValue as s } from "../../hooks/use-latest-value.js";
import { useServerHandoffComplete as l } from "../../hooks/use-server-handoff-complete.js";
import { useSyncRefs as y } from "../../hooks/use-sync-refs.js";
import { useTransition as R, transitionDataAttributes as H } from "../../hooks/use-transition.js";
import { State as i, OpenClosedProvider as c, useOpenClosed as u } from "../../internal/open-closed.js";
import { classNames as t } from "../../utils/class-names.js";
import { match as u$1 } from "../../utils/match.js";
import { RenderFeatures as M$1, forwardRefWithAs as W, RenderStrategy as O, compact as m, render as H$1 } from "../../utils/render.js";
function ue(e) {
  var t2;
  return !!(e.enter || e.enterFrom || e.enterTo || e.leave || e.leaveFrom || e.leaveTo) || ((t2 = e.as) != null ? t2 : de) !== Fragment || React__default.Children.count(e.children) === 1;
}
let w = createContext(null);
w.displayName = "TransitionContext";
var Ne = ((n2) => (n2.Visible = "visible", n2.Hidden = "hidden", n2))(Ne || {});
function _e() {
  let e = useContext(w);
  if (e === null) throw new Error("A <Transition.Child /> is used but it is missing a parent <Transition /> or <Transition.Root />.");
  return e;
}
function De() {
  let e = useContext(M);
  if (e === null) throw new Error("A <Transition.Child /> is used but it is missing a parent <Transition /> or <Transition.Root />.");
  return e;
}
let M = createContext(null);
M.displayName = "NestingContext";
function U(e) {
  return "children" in e ? U(e.children) : e.current.filter(({ el: t2 }) => t2.current !== null).filter(({ state: t2 }) => t2 === "visible").length > 0;
}
function Te(e, t2) {
  let n2 = s(e), l2 = useRef([]), y2 = f(), R2 = p(), T = o((o2, i2 = O.Hidden) => {
    let a = l2.current.findIndex(({ el: s2 }) => s2 === o2);
    a !== -1 && (u$1(i2, { [O.Unmount]() {
      l2.current.splice(a, 1);
    }, [O.Hidden]() {
      l2.current[a].state = "hidden";
    } }), R2.microTask(() => {
      var s2;
      !U(l2) && y2.current && ((s2 = n2.current) == null || s2.call(n2));
    }));
  }), P = o((o2) => {
    let i2 = l2.current.find(({ el: a }) => a === o2);
    return i2 ? i2.state !== "visible" && (i2.state = "visible") : l2.current.push({ el: o2, state: "visible" }), () => T(o2, O.Unmount);
  }), p$1 = useRef([]), m2 = useRef(Promise.resolve()), C = useRef({ enter: [], leave: [] }), h = o((o2, i2, a) => {
    p$1.current.splice(0), t2 && (t2.chains.current[i2] = t2.chains.current[i2].filter(([s2]) => s2 !== o2)), t2 == null || t2.chains.current[i2].push([o2, new Promise((s2) => {
      p$1.current.push(s2);
    })]), t2 == null || t2.chains.current[i2].push([o2, new Promise((s2) => {
      Promise.all(C.current[i2].map(([r, d]) => d)).then(() => s2());
    })]), i2 === "enter" ? m2.current = m2.current.then(() => t2 == null ? void 0 : t2.wait.current).then(() => a(i2)) : a(i2);
  }), g = o((o2, i2, a) => {
    Promise.all(C.current[i2].splice(0).map(([s2, r]) => r)).then(() => {
      var s2;
      (s2 = p$1.current.shift()) == null || s2();
    }).then(() => a(i2));
  });
  return useMemo(() => ({ children: l2, register: P, unregister: T, onStart: h, onStop: g, wait: m2, chains: C }), [P, T, l2, h, g, C, m2]);
}
let de = Fragment, fe = M$1.RenderStrategy;
function He(e, t$1) {
  var ee, te;
  let { transition: n$1 = true, beforeEnter: l$1, afterEnter: y$1, beforeLeave: R$1, afterLeave: T, enter: P, enterFrom: p2, enterTo: m$1, entered: C, leave: h, leaveFrom: g, leaveTo: o$1, ...i$1 } = e, [a, s2] = useState(null), r = useRef(null), d = ue(e), j = y(...d ? [r, t$1, s2] : t$1 === null ? [] : [t$1]), v = (ee = i$1.unmount) == null || ee ? O.Unmount : O.Hidden, { show: c$1, appear: z, initial: K } = _e(), [b, G] = useState(c$1 ? "visible" : "hidden"), Q = De(), { register: A, unregister: I } = Q;
  n(() => A(r), [A, r]), n(() => {
    if (v === O.Hidden && r.current) {
      if (c$1 && b !== "visible") {
        G("visible");
        return;
      }
      return u$1(b, { ["hidden"]: () => I(r), ["visible"]: () => A(r) });
    }
  }, [b, r, A, I, c$1, v]);
  let B = l();
  n(() => {
    if (d && B && b === "visible" && r.current === null) throw new Error("Did you forget to passthrough the `ref` to the actual DOM node?");
  }, [r, b, B, d]);
  let ce = K && !z, Y = z && c$1 && K, W2 = useRef(false), L = Te(() => {
    W2.current || (G("hidden"), I(r));
  }, Q), Z = o((k) => {
    W2.current = true;
    let F = k ? "enter" : "leave";
    L.onStart(r, F, (D) => {
      D === "enter" ? l$1 == null || l$1() : D === "leave" && (R$1 == null || R$1());
    });
  }), $ = o((k) => {
    let F = k ? "enter" : "leave";
    W2.current = false, L.onStop(r, F, (D) => {
      D === "enter" ? y$1 == null || y$1() : D === "leave" && (T == null || T());
    }), F === "leave" && !U(L) && (G("hidden"), I(r));
  });
  useEffect(() => {
    d && n$1 || (Z(c$1), $(c$1));
  }, [c$1, d, n$1]);
  let pe = /* @__PURE__ */ (() => !(!n$1 || !d || !B || ce))(), [, u2] = R(pe, a, c$1, { start: Z, end: $ }), Ce = m({ ref: j, className: ((te = t(i$1.className, Y && P, Y && p2, u2.enter && P, u2.enter && u2.closed && p2, u2.enter && !u2.closed && m$1, u2.leave && h, u2.leave && !u2.closed && g, u2.leave && u2.closed && o$1, !u2.transition && c$1 && C)) == null ? void 0 : te.trim()) || void 0, ...H(u2) }), _ = 0;
  return b === "visible" && (_ |= i.Open), b === "hidden" && (_ |= i.Closed), u2.enter && (_ |= i.Opening), u2.leave && (_ |= i.Closing), React__default.createElement(M.Provider, { value: L }, React__default.createElement(c, { value: _ }, H$1({ ourProps: Ce, theirProps: i$1, defaultTag: de, features: fe, visible: b === "visible", name: "Transition.Child" })));
}
function Ae(e, t2) {
  let { show: n$1, appear: l$1 = false, unmount: y$1 = true, ...R2 } = e, T = useRef(null), P = ue(e), p2 = y(...P ? [T, t2] : t2 === null ? [] : [t2]);
  l();
  let m2 = u();
  if (n$1 === void 0 && m2 !== null && (n$1 = (m2 & i.Open) === i.Open), n$1 === void 0) throw new Error("A <Transition /> is used but it is missing a `show={true | false}` prop.");
  let [C, h] = useState(n$1 ? "visible" : "hidden"), g = Te(() => {
    n$1 || h("hidden");
  }), [o$1, i$1] = useState(true), a = useRef([n$1]);
  n(() => {
    o$1 !== false && a.current[a.current.length - 1] !== n$1 && (a.current.push(n$1), i$1(false));
  }, [a, n$1]);
  let s2 = useMemo(() => ({ show: n$1, appear: l$1, initial: o$1 }), [n$1, l$1, o$1]);
  n(() => {
    n$1 ? h("visible") : !U(g) && T.current !== null && h("hidden");
  }, [n$1, g]);
  let r = { unmount: y$1 }, d = o(() => {
    var v;
    o$1 && i$1(false), (v = e.beforeEnter) == null || v.call(e);
  }), j = o(() => {
    var v;
    o$1 && i$1(false), (v = e.beforeLeave) == null || v.call(e);
  });
  return React__default.createElement(M.Provider, { value: g }, React__default.createElement(w.Provider, { value: s2 }, H$1({ ourProps: { ...r, as: Fragment, children: React__default.createElement(me, { ref: p2, ...r, ...R2, beforeEnter: d, beforeLeave: j }) }, theirProps: {}, defaultTag: Fragment, features: fe, visible: C === "visible", name: "Transition" })));
}
function Ie(e, t2) {
  let n2 = useContext(w) !== null, l2 = u() !== null;
  return React__default.createElement(React__default.Fragment, null, !n2 && l2 ? React__default.createElement(X, { ref: t2, ...e }) : React__default.createElement(me, { ref: t2, ...e }));
}
let X = W(Ae), me = W(He), Le = W(Ie), Xe = Object.assign(X, { Child: Le, Root: X });
export {
  Xe as Transition,
  Le as TransitionChild
};
